<script setup lang="ts">
import { cn } from '@/lib/utils'
import { X } from 'lucide-vue-next'
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

const props = defineProps<DialogContentProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props
  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 bg-white/85 dark:bg-white/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'fixed z-50 w-full gap-4 border bg-background p-6 shadow-lg',
          // Base animation classes
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:duration-200 data-[state=closed]:duration-200',
          // Mobile: bottom sheet
          'left-0 right-0 bottom-0 max-w-full rounded-t-2xl',
          'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
          // Desktop: centered modal
          'sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-w-lg',
          'sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:rounded-lg',
          'sm:data-[state=open]:fade-in-0 sm:data-[state=closed]:fade-out-0',
          'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
          'sm:data-[state=open]:slide-in-from-left-1/2 sm:data-&lsqb;state=open&rsqb;:slide-in-from-top-[48%]',
          'sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-&lsqb;state=closed&rsqb;:slide-out-to-top-[48%]',
          props.class,
        )"
    >
      <slot />
      <DialogClose
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
      >
      <X class="w-12 h-12 text-muted-foreground bg-background/90 text-foreground p-2 rounded-full" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>